import { CircularProgress } from '@cere/cere-design-system';
import { privacyColors } from './MemoryGraphCanvas';
import { MemoryEvidenceStrip, type MemoryCiter } from './MemoryEvidenceStrip';
import { relativeUpdated } from './recency';

/**
 * One selected memory record, in full — the read half of the explore UI.
 * Purely presentational: the section owns the `record_get` read, the
 * refusal/fault semantics, and the fallback record when the full row is
 * hidden; this pane renders whatever it is handed, never fetches.
 *
 * Tier 2 adds two lines under the record's own provenance: a RECENCY chip
 * ("updated 3d ago", off `updated_at` when it is known) and the EVIDENCE
 * strip ("cited by … — in current view", computed client-side from loaded
 * edges by the section). Both are absent when their input is absent — no
 * empty speculation.
 */

/** The full `record_get` row. Body/timestamps are null when the read was
 *  refused (the record was disclosed by a list/neighbours projection, but
 *  its full row is not readable) — the pane shows what is held, no error. */
export interface MemoryRecordDetail {
  id: string;
  type: string | null;
  title: string | null;
  body: string | null;
  scope: string | null;
  privacy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/** `2026-09-15T09:30:00Z` → `2026-09-15 09:30` — date and minute, the two
 *  parts a human compares; the seconds and Z are noise at this size. */
function formatTs(iso: string): string {
  return iso.replace('T', ' ').slice(0, 16);
}

export function MemoryDetailPane({ record, loading, citers = [], onCite }: {
  record: MemoryRecordDetail | null;
  loading: boolean;
  /** Loaded records whose edges point at this one ("in current view"). */
  citers?: MemoryCiter[];
  /** Selects (and scrolls to) a citing record. */
  onCite?: (id: string) => void;
}) {
  if (loading && record === null) {
    return (
      <div className="vault-orgs__memory-pane vault-orgs__memory-pane--loading" data-testid="memory-detail-loading">
        <CircularProgress size={20} />
      </div>
    );
  }
  if (record === null) return null;

  const colors = privacyColors(record.privacy);
  // The recency chip: relative, coarse, and only when `updated_at` is known
  // (a projection fallback carries none — no chip beats a wrong chip).
  const recency = relativeUpdated(record.updatedAt);
  return (
    <div className="vault-orgs__memory-pane" data-testid="memory-detail-pane">
      <div className="vault-orgs__memory-pane-head">
        <span className="vault-orgs__memory-pane-type">{record.type}</span>
        {record.privacy !== null && (
          <span
            className="vault-orgs__memory-pane-privacy"
            style={{ background: colors.fill, borderColor: colors.stroke }}
          >
            {record.privacy}
          </span>
        )}
        {recency !== null && (
          <span className="vault-orgs__memory-pane-recency" data-testid="memory-recency">
            {recency}
          </span>
        )}
      </div>
      <h4 className="vault-orgs__memory-pane-title">{record.title || record.id}</h4>
      {/* The id is visible on purpose: demo audiences are technical, and the
          id is what they would take to the API/docs next. */}
      <code className="vault-orgs__memory-pane-id" data-testid="memory-detail-id">{record.id}</code>
      {record.scope !== null && <p className="vault-orgs__memory-pane-scope">{record.scope}</p>}
      {record.body !== null && (
        <p className="vault-orgs__memory-pane-body" data-testid="memory-detail-body">{record.body}</p>
      )}
      <p className="vault-orgs__memory-pane-times">
        {record.createdAt !== null && <>Created {formatTs(record.createdAt)}</>}
        {record.createdAt !== null && record.updatedAt !== null && <> · </>}
        {record.updatedAt !== null && <>Updated {formatTs(record.updatedAt)}</>}
      </p>
      {/* The evidence strip (tier 2): rendered only when the current view
          holds citers — the section computes them, this pane never guesses. */}
      <MemoryEvidenceStrip citers={citers} onCite={onCite ?? (() => {})} />
    </div>
  );
}
